import React, { useCallback, useMemo } from 'react';
import { showAlert, showConfirm } from '@/utils/common/alert';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { DESKTOP_CONFIRM_COPY } from '@/constants/admin';
import { useCreateResourceMutation, convertToFormData } from '@/store/api/admin/resourcesApi';
import { normalizeFilesForUpload } from '@/utils/admin/files';
import { IFile, IResourceFormValues } from '@/types/admin/resources';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useCreateResourceForm } from '@/components/admin/resources/CreateResource/hooks/useCreateResourceForm';
import {
  ResourceDetailsSection,
  ResourceContentSection,
  ResourceFilesSection,
} from '@/components/admin/resources/CreateResource/components';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';

type CreateResourceScreenProps = StackScreenProps<AdminStackParamList, 'CreateResource'>;

export const CreateResourceScreen: React.FC<CreateResourceScreenProps> = ({
  navigation,
  route,
}) => {
  const { highestPosition } = route.params || {};
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;

  const [createResource, { isLoading }] = useCreateResourceMutation();

  // Custom hook for form logic
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
  } = useCreateResourceForm((highestPosition !== undefined ? highestPosition + 1 : 0));

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedPropertyId) {
      showAlert('Error', 'No property selected');
      return;
    }

    try {
      // Normalize files to IFile[] for type compatibility and ensure web parts
      const normalizedFiles: IFile[] = await normalizeFilesForUpload(formData.files || []);

      const adjusted: IResourceFormValues = {
        ...formData,
        files: normalizedFiles,
      } as unknown as IResourceFormValues;

      // Convert form data to FormData for API
      const resourceFormData = convertToFormData(adjusted, selectedPropertyId);

      const result = await createResource(resourceFormData).unwrap();

      if (result?.success !== false) {
        showSuccess('Resource created successfully!');
        navigation.goBack();
      }
    } catch (error: any) {
      console.error('Create resource error:', error);
      showApiError(error, 'Failed to create resource');
    }
  }, [formData, validateForm, selectedPropertyId, createResource, showSuccess, showApiError, navigation]);

  // Double-confirm discard on cancel
  const confirmDiscardTwice = useCallback(async (onConfirm: () => void) => {
    const confirmed = await showConfirm(
      DESKTOP_CONFIRM_COPY.discardTitle,
      DESKTOP_CONFIRM_COPY.discardMessage,
      DESKTOP_CONFIRM_COPY.discardConfirmText,
      DESKTOP_CONFIRM_COPY.discardCancelText
    );

    if (confirmed) {
      onConfirm();
    }
  }, []);

  const handleCancel = useCallback(() => {
    confirmDiscardTwice(() => navigation.goBack());
  }, [confirmDiscardTwice, navigation]);

  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => (
    <>
      <ResourceDetailsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />

      <ResourceContentSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />

      <ResourceFilesSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
    </>
  ), [
    formData,
    errors,
    handleFieldChange,
  ]);

  return (
    <MobileFormScreen
      screenName="CreateResource"
      navigation={navigation}
      isSubmitting={isLoading}
      submitText="Create"
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    >
      {scrollContent}
    </MobileFormScreen>
  );
};
